import {
  processFieldHealthImageryForField,
  processFieldViewImagery,
  processSoilSampleImageryForField,
  processSsurgoImageryForField,
  processNonSatImageryForField,
  processImportedMapsGeoJsonForField,
  processFieldViewLegend
} from 'imagery/processors';
import {
  fetchFieldViewLegend,
  fetchFieldHealthImageryByFieldId,
  fetchFieldViewImagery,
  fetchNonSatImageryByFieldId,
  fetchSoilSampleImageryByFieldId,
  fetchSsurgoImageryByFieldId,
  fetchImportedMapsImageryByFieldId,
  fetchImportedMapsGeoJson
} from 'imagery/loaders';
import {
  getFieldHealthImageryByFieldId,
  getFieldViewImageryByFieldId,
  getSsurgoImageryByFieldId,
  getSoilSampleImageryByFieldId,
  getNonSatImageryByFieldId,
  getImportedMapsImageryByFieldId,
  getScoutingImageryForActiveField
} from 'imagery/selectors';
import {
  parseImportedMapsList,
  parseImportedMapsGeoJson
} from 'imagery/mosaicImageParser';
import { getActiveUserId } from 'current/selectors';
import { isSsurgoAllowed, getMosaicV3FHIFlag, getMosaicV4SsurgoFlag } from 'user/selectors';
import { isEmpty } from 'lodash';
import { treatErrorAs } from 'api/errors';
import { findResource } from 'loading/finders';
import { errorLevel } from 'constants/errors';
import { fieldHealthLayers } from 'constants/imagery';
import { resourceType } from 'constants/loading';

// FieldView legends are a bit weird since we mash them into other data.  Let's not do that again.
export const findFieldViewLegendForImage = ({ dispatch, getState, image }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    if( image.legend && !isEmpty(image.legend) ) {
      return image.legend;
    }
    const state = getState();
    const userId = getActiveUserId(state);
    const layer = image.layer;
    return fetchFieldViewLegend({
      userId,
      username: image.username,
      fieldViewUuid: image.fieldViewUuid,
      year: image.year,
      crop: image.crop,
      fieldViewLayer: image.fieldViewLayer
    })
    .then( (rawLegend) => processFieldViewLegend({ dispatch, getState, image, rawLegend, layer }) );
  });
};

// FieldView image data is ALSO a little weird, because we overlap with yield data   TODO: Merge them
export const findFieldViewImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const images = getFieldViewImageryByFieldId(state, fieldId);
    if( !isEmpty(images) ) {
      return images;
    }
    const userId = getActiveUserId(state);
    return fetchFieldViewImagery({ userId, fieldId })
    .then( (rawImageData) => processFieldViewImagery({ dispatch, getState, rawImageData }));
  });
};

export const findFieldHealthImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const images = getFieldHealthImageryByFieldId(state, fieldId);
    if( !isEmpty(images) ) {
      return images;
    }
    const mosaicV3FHIFlag = getMosaicV3FHIFlag(state);
    const userId = getActiveUserId(state);
    return fetchFieldHealthImageryByFieldId({ userId, fieldId, useV3: mosaicV3FHIFlag })
    .then( (rawImageData) => processFieldHealthImageryForField({ dispatch, getState, rawImageData, fieldId }));
  });
};

export const findScoutingImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  const state = getState();
  const userId = getActiveUserId(state);
  const mosaicV3FHIFlag = getMosaicV3FHIFlag(state);
  const resourceSpec = {
    type: `${resourceType.SCOUTING_IMAGERY}_${fieldId}`,
    selector: () => getScoutingImageryForActiveField(state),
    fetcher: () => fetchFieldHealthImageryByFieldId(
      { userId, fieldId, fieldHealthLayer: fieldHealthLayers.SCOUTING, useV3: mosaicV3FHIFlag }
    ),
    processor: (rawImageData) => processFieldHealthImageryForField({ dispatch, getState, rawImageData, fieldId }),
    forceWait: true,
    onError: treatErrorAs({ severity: errorLevel.BLOCK })
  };
  
  return findResource({ dispatch, getState, ...resourceSpec });
};

export const findSoilSampleImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const images = getSoilSampleImageryByFieldId(state, fieldId);
    if( !isEmpty(images) ) {
      return images;
    }
    const userId = getActiveUserId(state);
    return fetchSoilSampleImageryByFieldId({ userId, fieldId })
    .then( (rawImageData) => processSoilSampleImageryForField({ dispatch, getState, rawImageData, fieldId }));
  });
};

export const findNonSatImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  return Promise.resolve() // force a thenable wrapper
  .then( () => {
    const state = getState();
    const images = getNonSatImageryByFieldId(state, fieldId);

    if( !isEmpty(images) ) {
      return images;
    }
    const userId = getActiveUserId(state);
    return fetchNonSatImageryByFieldId({ userId, fieldId })
    .then( (rawImageData) => processNonSatImageryForField({ dispatch, getState, rawImageData, fieldId }));
  });
};

export const findSsurgoImageryByFieldId = async ({ dispatch, getState, fieldId }) => {
  const state = getState();

  if(!isSsurgoAllowed(state)) {
    return {};
  }

  const images = getSsurgoImageryByFieldId(state, fieldId);
  if (!isEmpty(images)) {
    return images;
  }
  const userId = getActiveUserId(state);
  const useMosaicV4 = getMosaicV4SsurgoFlag(state);
  const rawImageData = await fetchSsurgoImageryByFieldId({ userId, fieldId, useMosaicV4 });
  
  return processSsurgoImageryForField({ dispatch, getState, rawImageData, fieldId, useMosaicV4 });
};

export const findImportedMapsImageryByFieldId = ({ dispatch, getState, fieldId }) => {
  return Promise.resolve()
  .then(() => {
    const state = getState();

    const images = getImportedMapsImageryByFieldId(state, fieldId);
    if (!isEmpty(images)) {
      return images;
    }
    const userId = getActiveUserId(state);
    return fetchImportedMapsImageryByFieldId({ userId, fieldId })
    .then(rawData => parseImportedMapsList(rawData))
    .then((mapsWithoutGeoJson) => {
      if (mapsWithoutGeoJson.length > 0) {
        return fetchImportedMapsGeoJson({ mapsWithoutGeoJson })
        .then(rawGeoJsons => parseImportedMapsGeoJson(rawGeoJsons, mapsWithoutGeoJson));
      }
      return Promise.resolve([]);
    })
    .then(parsedImportedMaps => processImportedMapsGeoJsonForField({ dispatch, fieldId, parsedImportedMaps }));
  });
};
